// DNA Mapa — Inteligência Pedagógica e Mapeamento das Necessidades de Aprendizagem.
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, BrainCircuit, HelpCircle, Loader2, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { C, ModuleHeader, Notice, PageShell } from '@/design-system/dna';
import { errMsg } from './shared';
import { DiagnosticoTab } from './DiagnosticoTab';
import { PlanosTab } from './PlanosTab';
import { PainelTab } from './PainelTab';
import { BancoTab } from './BancoTab';
import { CienciaReport, InclusaoTab } from './InclusaoTab';
import { ConselhoTab } from './ConselhoTab';
import { HelpProvider } from './Help';
import { HelpCenter } from './HelpCenter';
import { Tour } from './Tour';

const saveOnb = (status, step) => api.put('/dna-mapa/onboarding', { status, step }).catch(() => {});

export default function DnaMapa() {
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('diagnostico');
  const [tour, setTour] = useState(null);
  const [center, setCenter] = useState(null);
  const year = new Date().getFullYear();
  const equipe = window.location.pathname.startsWith('/equipe');
  useEffect(() => { api.get('/dna-mapa/contexto', { params: { year } }).then(({ data }) => setCtx(data)).catch((e) => setErr(errMsg(e, 'Falha ao carregar o DNA Mapa.'))); }, [year]);
  useEffect(() => {
    if (!ctx) return undefined;
    let t;
    let off = false;
    const begin = (start) => { t = setTimeout(() => { if (off) return; if (document.querySelector('[role="dialog"]:not([data-testid="dna-tour"]), [data-testid="whats-new"]')) begin(start); else setTour((cur) => cur || { start }); }, 900); };
    api.get('/dna-mapa/onboarding').then(({ data }) => { if (!off && ['novo', 'em_andamento'].includes(data.status)) begin(data.step || 0); }).catch(() => {});
    return () => { off = true; clearTimeout(t); };
  }, [ctx]);
  const restartTour = useCallback(() => { setCenter(null); setTab('diagnostico'); saveOnb('em_andamento', 0); setTimeout(() => setTour({ start: 0 }), 350); }, []);
  const openCenter = useCallback((section = 'primeiros_passos') => setCenter(section), []);
  const tabs = [['diagnostico', 'Diagnóstico da turma'], ['planos', 'Intervenções'], ['inclusao', 'Documentação e Inclusão'], ['conselho', 'Conselho de Classe'], ...(ctx?.full ? [['painel', 'Painel da coordenação'], ['ciencia', 'Relatório de Ciência']] : []), ['banco', 'Banco pedagógico']];
  return (
    <HelpProvider actions={{ openCenter }}>
      <PageShell testid="dna-mapa" className={equipe ? 'px-3 pt-4 sm:px-6' : ''}>
        {equipe && <Button variant="ghost" className="w-fit" onClick={() => window.location.assign('/equipe/portal')} data-testid="dna-mapa-voltar-portal"><ArrowLeft className="h-4 w-4" /> Voltar ao Portal Equipe</Button>}
        <ModuleHeader kicker="Diagnóstico e Aprendizagem" title="DNA Mapa" icon={BrainCircuit} testid="dna-mapa-header"
          subtitle="Quem precisa de apoio, em qual conteúdo, por qual motivo, com qual estratégia — e se a aprendizagem avançou." />
        <div className="flex items-center gap-2 text-[12px]" style={{ color: C.muted }} data-testid="dna-mapa-lgpd"><ShieldCheck className="h-4 w-4" style={{ color: C.green }} /> Dados pedagógicos restritos à equipe autorizada · não publicados no Portal das Famílias · LGPD.</div>
        {err && <Notice tone="danger" testid="dna-mapa-erro">{err}</Notice>}
        {!ctx && !err && <p className="flex items-center gap-2 py-12 text-sm" style={{ color: C.muted }}><Loader2 className="h-4 w-4 animate-spin" /> Carregando atribuições…</p>}
        {ctx && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-2xl border bg-white p-1.5" style={{ borderColor: C.line }} data-testid="dna-mapa-tabs">
              {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-xl px-4 py-2 data-[state=active]:bg-[#2A1240] data-[state=active]:text-white" data-testid={`dna-mapa-tab-${k}`}>{l}</TabsTrigger>)}
            </TabsList>
            <TabsContent value="diagnostico" className="mt-4"><DiagnosticoTab ctx={ctx} /></TabsContent>
            <TabsContent value="planos" className="mt-4">{tab === 'planos' && <PlanosTab ctx={ctx} />}</TabsContent>
            {ctx.full && <TabsContent value="painel" className="mt-4">{tab === 'painel' && <PainelTab ctx={ctx} />}</TabsContent>}
            <TabsContent value="conselho" className="mt-4">{tab === 'conselho' && <ConselhoTab ctx={ctx} />}</TabsContent>
            <TabsContent value="inclusao" className="mt-4">{tab === 'inclusao' && <InclusaoTab />}</TabsContent>
            {ctx.full && <TabsContent value="ciencia" className="mt-4">{tab === 'ciencia' && <CienciaReport />}</TabsContent>}
            <TabsContent value="banco" className="mt-4">{tab === 'banco' && <BancoTab />}</TabsContent>
          </Tabs>
        )}
        {(ctx || err) && (
          <button type="button" onClick={() => openCenter()} data-testid="dna-help-fab"
            className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full px-4 py-3 text-[13px] font-bold text-white shadow-[0_14px_40px_-12px_rgba(42,18,64,.7)] transition-transform duration-200 hover:-translate-y-0.5 sm:bottom-6 sm:right-6"
            style={{ background: C.ink }}>
            <HelpCircle className="h-4 w-4" /> <span className="hidden sm:inline">Ajuda / Como preencher?</span><span className="sm:hidden">Ajuda</span>
          </button>
        )}
        <HelpCenter open={!!center} section={center} onClose={() => setCenter(null)} onRestartTour={restartTour} />
        {tour && ctx && <Tour start={tour.start} onStep={(n) => saveOnb('em_andamento', n)}
          onSkip={(n) => { saveOnb('pulado', n); setTour(null); }} onDone={() => { saveOnb('concluido', 8); setTour(null); }} />}
      </PageShell>
    </HelpProvider>
  );
}
